// A server-sent-events reader for fetch bodies. EventSource cannot POST, and the
// chat endpoints are POSTs, so the stream is read and parsed here.
// Follows https://html.spec.whatwg.org/multipage/server-sent-events.html#parsing-an-event-stream

export interface SseEvent {
  /** The `event:` name; "message" when the block had none. */
  event: string;
  data: string;
}

/**
 * Yields each complete event of the stream. Comment lines (": ping") are
 * skipped. An event cut off by a dropped connection is discarded, never
 * dispatched half-way, as the spec requires.
 */
export async function* readSse(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<SseEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const onAbort = () => void reader.cancel().catch(() => undefined);
  signal?.addEventListener("abort", onAbort);

  let buffer = "";
  let event = "";
  let data: string[] = [];
  let sawCarriageReturn = false;
  let finished = false;

  const dispatch = (): SseEvent | null => {
    const out = data.length > 0 ? { event: event || "message", data: data.join("\n") } : null;
    event = "";
    data = [];
    return out;
  };

  const handleLine = (line: string): SseEvent | null => {
    if (line === "") return dispatch();
    if (line.startsWith(":")) return null;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    return null;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        finished = true;
        break;
      }
      const decoded = decoder.decode(value, { stream: true });
      let text = decoded;
      // A CRLF split across two chunks must not produce an empty line.
      if (sawCarriageReturn && text.startsWith("\n")) text = text.slice(1);
      // An empty decode (a chunk that ended mid-character) says nothing about line endings.
      if (decoded) sawCarriageReturn = text.endsWith("\r");
      buffer += text;

      let start = 0;
      for (let i = 0; i < buffer.length; i++) {
        const ch = buffer[i];
        if (ch !== "\n" && ch !== "\r") continue;
        const dispatched = handleLine(buffer.slice(start, i));
        if (ch === "\r" && buffer[i + 1] === "\n") i++;
        start = i + 1;
        if (dispatched) yield dispatched;
      }
      buffer = buffer.slice(start);
    }
  } finally {
    signal?.removeEventListener("abort", onAbort);
    // Stopping early (the consumer left, or an error was thrown) must close the
    // connection rather than leave the response streaming into nowhere.
    if (!finished) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
